import React, { useEffect, useRef, useState } from 'react';
import { Triangle, Color } from 'ogl';
import { useWebGLRenderer } from '../hooks/useWebGLRenderer';
import './Threads.css';

const vertexShader = `
attribute vec2 position;
attribute vec2 uv;
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4(position, 0.0, 1.0);
}
`;

const fragmentShader = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif

uniform float iTime;
uniform vec3 iResolution;
uniform vec3 uColor;
uniform float uAmplitude;
uniform float uDistance;
uniform vec2 uMouse;

#define PI 3.1415926538

const int u_line_count = 40;
const float u_line_width = 7.0;
const float u_line_blur = 10.0;

float Perlin2D(vec2 P) {
    vec2 Pi = floor(P);
    vec4 Pf_Pfmin1 = P.xyxy - vec4(Pi, Pi + 1.0);
    vec4 Pt = vec4(Pi.xy, Pi.xy + 1.0);
    Pt = Pt - floor(Pt * (1.0 / 71.0)) * 71.0;
    Pt += vec2(26.0, 161.0).xyxy;
    Pt *= Pt;
    Pt = Pt.xzxz * Pt.yyww;
    vec4 hash_x = fract(Pt * (1.0 / 951.135664));
    vec4 hash_y = fract(Pt * (1.0 / 642.949883));
    vec4 grad_x = hash_x - 0.49999;
    vec4 grad_y = hash_y - 0.49999;
    vec4 grad_results = inversesqrt(grad_x * grad_x + grad_y * grad_y)
        * (grad_x * Pf_Pfmin1.xzxz + grad_y * Pf_Pfmin1.yyww);
    grad_results *= 1.4142135623730950;
    vec2 blend = Pf_Pfmin1.xy * Pf_Pfmin1.xy * Pf_Pfmin1.xy
               * (Pf_Pfmin1.xy * (Pf_Pfmin1.xy * 6.0 - 15.0) + 10.0);
    vec4 blend2 = vec4(blend, vec2(1.0 - blend));
    return dot(grad_results, blend2.zxzx * blend2.wwyy);
}

float pixel(float count, vec2 resolution) {
    return (1.0 / max(resolution.x, resolution.y)) * count;
}

float lineFn(vec2 st, float width, float perc, float offset, vec2 mouse, float time, float amplitude, float distance) {
    float split_offset = (perc * 0.4);
    float split_point = 0.1 + split_offset;

    float amplitude_normal = smoothstep(split_point, 0.7, st.x);
    float amplitude_strength = 0.5;
    float finalAmplitude = amplitude_normal * amplitude_strength
                           * amplitude * (1.0 + (mouse.y - 0.5) * 0.2);

    float time_scaled = time / 10.0 + (mouse.x - 0.5) * 1.0;
    float blur = smoothstep(split_point, split_point + 0.05, st.x) * perc;

    float xnoise = mix(
        Perlin2D(vec2(time_scaled, st.x + perc) * 2.5),
        Perlin2D(vec2(time_scaled, st.x + time_scaled) * 3.5) / 1.5,
        st.x * 0.3
    );

    float y = 0.5 + (perc - 0.5) * distance + xnoise / 2.0 * finalAmplitude;

    float line_start = smoothstep(
        y + (width / 2.0) + (u_line_blur * pixel(1.0, iResolution.xy) * blur),
        y,
        st.y
    );

    float line_end = smoothstep(
        y,
        y - (width / 2.0) - (u_line_blur * pixel(1.0, iResolution.xy) * blur),
        st.y
    );

    return clamp(
        (line_start - line_end) * (1.0 - smoothstep(0.0, 1.0, pow(perc, 0.3))),
        0.0,
        1.0
    );
}

void mainImage(out vec4 fragColor, in vec2 fragCoord) {
    vec2 uv = fragCoord / iResolution.xy;

    float line_strength = 1.0;
    for (int i = 0; i < u_line_count; i++) {
        float p = float(i) / float(u_line_count);
        line_strength *= (1.0 - lineFn(
            uv,
            u_line_width * pixel(1.0, iResolution.xy) * (1.0 - p),
            p,
            (PI * 1.0) * p,
            uMouse,
            iTime,
            uAmplitude,
            uDistance
        ));
    }

    float colorVal = 1.0 - line_strength;
    fragColor = vec4(uColor * colorVal, colorVal);
}

void main() {
    mainImage(gl_FragColor, gl_FragCoord.xy);
}
`;

interface ThreadsProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'color'> {
  color?: [number, number, number];
  amplitude?: number;
  distance?: number;
  enableMouseInteraction?: boolean;
}

const Threads: React.FC<ThreadsProps> = ({ 
  color = [1, 1, 1], 
  amplitude = 1, 
  distance = 0, 
  enableMouseInteraction = false, 
  ...rest 
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const currentMouse = useRef([0.5, 0.5]);
  const targetMouse = useRef([0.5, 0.5]);
  const [_containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  const { canvasRef, programRef, hasError } = useWebGLRenderer({
    vertexShader,
    fragmentShader,
    geometry: (gl) => new Triangle(gl),
    uniforms: {
      iTime: { value: 0 },
      iResolution: { value: new Color(0, 0, 1) },
      uColor: { value: new Color(...color) },
      uAmplitude: { value: amplitude },
      uDistance: { value: distance },
      uMouse: { value: new Float32Array([0.5, 0.5]) }
    },
    alpha: true,
    onResize: (w, h, program) => {
      program.uniforms.iResolution.value.r = w;
      program.uniforms.iResolution.value.g = h;
      program.uniforms.iResolution.value.b = w / h;
      setContainerSize({ width: w, height: h });
    },
    onUpdate: (time, program) => {
      if (enableMouseInteraction) {
        const smoothing = 0.05;
        currentMouse.current[0] += smoothing * (targetMouse.current[0] - currentMouse.current[0]);
        currentMouse.current[1] += smoothing * (targetMouse.current[1] - currentMouse.current[1]);
        (program.uniforms.uMouse.value as Float32Array)[0] = currentMouse.current[0];
        (program.uniforms.uMouse.value as Float32Array)[1] = currentMouse.current[1];
      } else {
        (program.uniforms.uMouse.value as Float32Array)[0] = 0.5;
        (program.uniforms.uMouse.value as Float32Array)[1] = 0.5;
      }
      program.uniforms.iTime.value = time;
    }
  });
  

  
  // Mouse interaction handlers
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !enableMouseInteraction) {return;}
    
    const handleMouseMove = (e: MouseEvent) => {
      const rect = container.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = 1.0 - (e.clientY - rect.top) / rect.height;
      targetMouse.current = [x, y];
    };
    
    const handleMouseLeave = () => {
      targetMouse.current = [0.5, 0.5];
    };
    
    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseleave', handleMouseLeave);
    
    return () => {
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [enableMouseInteraction]);
  
  // Update uniforms when props change
  useEffect(() => {
    const program = programRef.current;
    if (!program) {return;}
    
    program.uniforms.uColor.value = new Color(...color);
    program.uniforms.uAmplitude.value = amplitude;
    program.uniforms.uDistance.value = distance;
  }, [color, amplitude, distance, programRef]);

  if (hasError) {
    return (
      <div 
        ref={containerRef} 
        className="threads-container" 
        style={{
          background: `linear-gradient(135deg, 
            rgba(${color[0] * 255}, ${color[1] * 255}, ${color[2] * 255}, 0.1) 0%, 
            rgba(${color[0] * 255}, ${color[1] * 255}, ${color[2] * 255}, 0.05) 100%)`,
        }}
        {...rest} 
      />
    );
  }

  return (
    <div ref={containerRef} className="threads-container" {...rest}>
      <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />
    </div>
  );
};

export default Threads;
